import {
  CourseId,
  CourseResponse,
  CourseStepResponse,
  CompactCourseResponse,
  UserCourseCountResponse,
  CourseSearchParams,
  VeryCompactCourseResponse,
} from '@business/entities/course';

import { Endpoint } from '../endpoint';
import { UserId } from '@business/entities/account';
import { R } from '@core/utils/r';

export function fetchCourses(params?: CourseSearchParams) {
  return Endpoint.get<CompactCourseResponse[]>('@makh/courses', { params: R.objectCompact(params) });
}

export function fetchCourse(id: CourseId) {
  return Endpoint.get<CourseResponse>(`@makh/courses/course/${id}`);
}

export function createCourse(payload: Partial<CourseResponse>, userId?: UserId) {
  const data = R.objectCompact({ ...payload, userId: userId });
  return Endpoint.post<CourseResponse>(`@makh/courses/create`, { data });
}

export function duplicateCourse(id: CourseId, data: Partial<CourseResponse>) {
  return Endpoint.post<CourseResponse>(`@makh/courses/duplicate-course/${id}`, { data });
}

export function updateCourse(id: CourseId, data: Partial<CourseResponse>) {
  return Endpoint.patch<CourseResponse>(`@makh/courses/${id}`, { data });
}

export function updateCourseArchive(id: CourseId, data: Partial<CourseResponse>) {
  return Endpoint.patch<CourseResponse>(`@makh/courses/course-archive/${id}`, { data });
}

export function deleteCourse(id: CourseId) {
  return Endpoint.delete<void>(`@makh/courses/${id}`);
}

/** Section **/
export function createCourseSection(id: CourseId, name: string) {
  return Endpoint.post<void>(`@makh/courses/section/${id}`, { data: { name } });
}

export function updateCourseSection(id: number, name: string) {
  return Endpoint.patch<void>(`@makh/courses/section/${id}`, { data: { name } });
}

export function deleteCourseSection(id: number) {
  return Endpoint.delete<void>(`@makh/courses/section/${id}`);
}

export function sortSections(courseId: CourseId, ids: number[]) {
  return Endpoint.post<void>(`@makh/courses/sort-section`, { data: { ids, courseId } });
}

/** Step **/
export function createCourseStep(id: number, data: Partial<CourseStepResponse>) {
  return Endpoint.post<CourseStepResponse>(`@makh/courses/step/${id}`, { data });
}

export function updateCourseStep(id: number, data: Partial<CourseStepResponse>) {
  return Endpoint.patch<void>(`@makh/courses/step/${id}`, { data });
}

export function updateCourseStepName(id: number, name: string) {
  return Endpoint.patch<void>(`@makh/courses/step-name/${id}`, { data: { name } });
}

export function deleteCourseStep(id: number) {
  return Endpoint.delete<void>(`@makh/courses/step/${id}`);
}

export function stepCopy(courseId: CourseId, id: number) {
  return Endpoint.post<CourseResponse>(`@makh/courses/step-copy/${id}`);
}

export function sortSectionSteps(courseId: CourseId, ids: number[]) {
  return Endpoint.post<void>(`@makh/courses/sort-step`, { data: { ids, courseId } });
}

export function updateAssignne(type: string, id: number, items: string[]) {
  return Endpoint.post<CourseResponse>(`@makh/courses/assignee`, { data: { type, id, items } });
}

/* User Courses */
export function enroll(id: CourseId, uids: UserId[]) {
  return Endpoint.post<CourseResponse>(`@makh/courses/enroll/${id}`, { data: { uids } });
}

export function unenroll(id: CourseId, uid: UserId) {
  return Endpoint.post<CourseResponse>(`@makh/courses/unenroll/${id}`, { data: { uid } });
}

export function restart(id: CourseId, uid: UserId) {
  return Endpoint.post<CourseResponse>(`@makh/courses/restart/${id}`, { data: { uid } });
}

export function bulkRestart(id: CourseId, uids: UserId[]) {
  return Endpoint.post<CourseResponse>(`@makh/courses/bulk-restart/${id}`, { data: { uids } });
}

export function fetchUserCourses(id: CourseId) {
  return Endpoint.get<UserCourseCountResponse>(`@makh/courses/course-count/${id}`);
}

export function updateFormData(id: string, data: string) {
  return Endpoint.post<void>(`@makh/courses/update-form-data/${id}`, { data: { data } });
}

export function getStep(id: string) {
  return Endpoint.get<CourseStepResponse>(`@makh/courses/step/${id}`);
}

export function sortUserCourses(userId: UserId, courseIds: CourseId[]) {
  return Endpoint.post<void>(`@makh/courses/sort-user-courses`, { data: { userId, courseIds } });
}

export function updateCourseStatus(id: CourseId, data: Partial<CourseResponse>) {
  return Endpoint.patch<CourseResponse>(`@makh/courses/course-status/${id}`, { data });
}

export function searchCourses(params?: CourseSearchParams) {
  return Endpoint.get<VeryCompactCourseResponse[]>('@makh/courses/search-courses', { params: R.objectCompact(params) });
}

export function generateCertificatePdf(id: CourseId) {
  return Endpoint.get<any>(`@makh/courses/generate-pdf/${id}`, {
    isFile: true,
    headers: {
      'Content-Type': 'application/pdf',
    },
  });
}
